import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, LinearProgress, Typography } from "@mui/material";
import { Loop as LoopIcon, OpenInNew } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import batchJobsService, { isTerminalJobStatus } from "../../services/batchJobsService";
import {
  formatJobElapsed,
  getJobPollIntervalMs,
  getJobStatusMeta,
  isJobStale,
} from "./batchJobUtils";

const FINAL_OUTPUT_PREVIEW_CHARS = 600;

/**
 * In-conversation card shown when a chain run ends with `batch_job_started`.
 * Polls the job until it reaches a terminal state, then shows a final_output preview.
 */
const BatchJobCard = ({ startedEvent, isDarkMode }) => {
  const jobId = startedEvent?.job_id;
  const [job, setJob] = useState(null);
  const [pollError, setPollError] = useState(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await batchJobsService.getJob(jobId);
      if (!mountedRef.current) return;
      setJob(data);
      setPollError(null);
      const interval = getJobPollIntervalMs(data.status);
      if (interval) {
        pollTimerRef.current = setTimeout(fetchJob, interval);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setPollError(error?.message || "Failed to fetch job status");
      // Keep trying — transient network errors shouldn't kill tracking.
      pollTimerRef.current = setTimeout(fetchJob, 30000);
    }
  }, [jobId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchJob();
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchJob]);

  if (!jobId) return null;

  const status = job?.status || "pending";
  const statusMeta = getJobStatusMeta(status);
  const totalItems = job?.total_items ?? startedEvent?.total_items ?? 0;
  const completedItems = job?.completed_items ?? 0;
  const failedItems = job?.failed_items ?? 0;
  const progressPct = job?.progress_pct ?? 0;
  const isTerminal = isTerminalJobStatus(status);
  const sourceMeta = startedEvent?.source_meta || job?.config?.source_meta || {};

  const finalOutput = job?.final_output;
  const finalOutputPreview =
    typeof finalOutput === "string" && finalOutput.length > FINAL_OUTPUT_PREVIEW_CHARS
      ? `${finalOutput.slice(0, FINAL_OUTPUT_PREVIEW_CHARS)}…`
      : finalOutput;

  const openDetail = () => {
    window.open(`/batch-jobs/${jobId}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isDarkMode ? "rgba(167, 139, 250, 0.35)" : "rgba(124, 58, 237, 0.25)",
        backgroundColor: isDarkMode ? "rgba(46, 16, 101, 0.18)" : "rgba(237, 233, 254, 0.4)",
        maxWidth: 640,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <LoopIcon sx={{ fontSize: 20, color: isDarkMode ? "#c4b5fd" : "#6d28d9" }} />
        <Typography variant="body2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {isTerminal
            ? "Batch job finished"
            : `Batch job started — processing ${totalItems.toLocaleString()} records in the background`}
        </Typography>
        <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
      </Box>

      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, progressPct))}
        sx={{ height: 8, borderRadius: 4, mb: 1 }}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {completedItems.toLocaleString()} / {totalItems.toLocaleString()} completed
        </Typography>
        {failedItems > 0 && (
          <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
            {failedItems.toLocaleString()} failed
          </Typography>
        )}
        {job?.started_at && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Elapsed {formatJobElapsed(job)}
          </Typography>
        )}
      </Box>

      {sourceMeta.truncated && (
        <Typography variant="caption" sx={{ display: "block", color: "warning.main", mb: 0.5 }}>
          Dataset truncated to {totalItems.toLocaleString()} records (max items cap).
        </Typography>
      )}
      {Number(sourceMeta.duplicates_removed) > 0 && (
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 0.5 }}>
          {Number(sourceMeta.duplicates_removed).toLocaleString()} duplicate records removed.
        </Typography>
      )}
      {isJobStale(job) && (
        <Typography variant="caption" sx={{ display: "block", color: "warning.main", mb: 0.5 }}>
          Recovering… the job has not reported progress recently; it will resume automatically.
        </Typography>
      )}
      {pollError && (
        <Typography variant="caption" sx={{ display: "block", color: "error.main", mb: 0.5 }}>
          {pollError}
        </Typography>
      )}
      {isTerminal && job?.error_message && (
        <Typography variant="caption" sx={{ display: "block", color: "error.main", mb: 0.5 }}>
          {job.error_message}
        </Typography>
      )}

      {isTerminal && finalOutputPreview && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 1,
            backgroundColor: isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.7)",
            "& p": { m: 0 },
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}
          >
            Result
          </Typography>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{finalOutputPreview}</ReactMarkdown>
        </Box>
      )}

      <Box sx={{ mt: 1.25 }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
          onClick={openDetail}
        >
          {isTerminal ? "View results" : "Track job"}
        </Button>
      </Box>
    </Box>
  );
};

export default BatchJobCard;
